import { Skeleton } from 'components/app-components/ui/skeleton';
import MemoizedCopyableText from 'components/generic-components/copyableText';
import { CardNumber } from './CardNumber';
import { CardCVV } from './CardCVV';

interface CardDetailsDisplayProps {
  cardNumberSeparated?: string[];
  formattedExpiry: string;
  cvv?: string;
  showDetails: boolean;
  isLoadingCardDetails: boolean;
  onToggleDetails: () => void;
  userName: string;
  addressToDisplay: string;
}

/**
 * Card visual display with all card details (number, expiry, CVV, name, address)
 */
export const CardDetailsDisplay = ({
  cardNumberSeparated,
  formattedExpiry,
  cvv,
  showDetails,
  isLoadingCardDetails,
  onToggleDetails,
  userName,
  addressToDisplay,
}: CardDetailsDisplayProps) => {
  const canRevealDetails = !isLoadingCardDetails;

  return (
    <div className="flex flex-col items-start gap-4 sm:flex-row">
      <div className="relative w-full min-w-[240px] shrink-0 sm:w-auto">
        <div
          role="button"
          onClick={canRevealDetails ? onToggleDetails : undefined}
          className={`relative ${canRevealDetails ? 'cursor-pointer' : ''}`}
        >
          <img src="/images/thera-card-bg.svg" alt="thera-card" className="w-full min-w-[240px] max-w-[320px]" />
          <div id="card-details" className="absolute bottom-4 left-4">
            <div className="flex flex-col gap-0.5">
              <CardNumber
                cardNumberSeparated={cardNumberSeparated}
                showDetails={showDetails}
                isLoadingCardDetails={isLoadingCardDetails}
              />
            </div>
            <div className="mt-3 flex">
              <div className="flex flex-[2] flex-col gap-0.5">
                <p className="text-xs font-medium text-neutral-400">Expiry</p>
                {isLoadingCardDetails ? (
                  <Skeleton className="h-3 w-12 bg-neutral-500" />
                ) : (
                  <p className="flex items-center gap-2 text-sm font-medium text-white">
                    {showDetails ? formattedExpiry : '••/••'}
                    {showDetails && formattedExpiry && formattedExpiry !== 'N/A' && (
                      <MemoizedCopyableText
                        text={formattedExpiry}
                        onlyIconMode={true}
                        showBorder={false}
                        iconStrokeColor="white"
                      />
                    )}
                  </p>
                )}
              </div>
              <div className="flex flex-[3] flex-col gap-0.5">
                <p className="text-xs font-medium text-neutral-400">CVV</p>
                <CardCVV cvv={cvv} showDetails={showDetails} isLoadingCardDetails={isLoadingCardDetails} />
              </div>
            </div>
          </div>
        </div>
        {!isLoadingCardDetails && (
          <p className="mt-2 text-xs text-neutral-500">
            {showDetails ? 'Click card to hide details' : 'Click card to reveal details'}
          </p>
        )}
        <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-neutral-600 bg-neutral-700 px-3 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-therablue" />
          <span className="text-xs font-medium text-neutral-300">Physical cards coming soon</span>
        </div>
      </div>
      <div className="flex w-full flex-col gap-4 sm:max-w-[200px]">
        <div className="flex flex-col items-start gap-1 text-xs">
          <p className="text-xs font-medium text-neutral-500">Name</p>
          <p className="text-sm font-medium text-neutral-200">{userName}</p>
        </div>
        <div className="flex flex-col items-start gap-1 text-xs">
          <p className="text-xs font-medium text-neutral-500">Address</p>
          <p className="text-sm font-medium text-neutral-200">{addressToDisplay}</p>
        </div>
      </div>
    </div>
  );
};
